import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaHome, FaBook, FaPlay, FaFileAlt, FaDesktop, FaCheckCircle, FaImage, FaRedo, FaCheck, FaStar, FaLightbulb, FaBookOpen, FaBolt, FaAward, FaExpand, FaCompress } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import BookReader from '../components/courses/BookReader';
import VideoPlayer from '../components/courses/VideoPlayer';
import PresentationViewer from '../components/courses/PresentationViewer';
import { getFlashcards, getIconType, ICON_TYPE } from '../data/flashcards';
import {
  LuCpu, LuBrain, LuPenTool, LuChartBar, LuCircleX, LuZap, LuCoins, LuUsers,
  LuGlobe, LuBuilding, LuTruck, LuHeartPulse, LuTrendingUp,
  LuFileText, LuPalette, LuSmartphone, LuMegaphone, LuCalendar, LuSearch,
  LuPercent, LuMessageSquare, LuSparkles, LuMail, LuTarget, LuPackage,
  LuVideo, LuCompass, LuShieldCheck, LuRocket,
  LuTrophy, LuThumbsUp, LuFlame,
} from 'react-icons/lu';

const categoryLabels = {
  'shapes': 'الأشكال', 'properties': 'الخصائص', 'objects': 'الأغراض', '3d': 'ثلاثي الأبعاد', 'patterns': 'الأنماط',
  'numbers-0-10': 'الأرقام ٠-١٠', 'numbers-11-20': 'الأرقام ١١-٢٠', 'counting': 'العدّ', 'addition': 'الجمع', 'subtraction': 'الطرح',
  'ch1-basics': 'الأساسيات', 'ch2-algeria': 'السوق الجزائري', 'ch3-tools': 'الأدوات', 'ch4-marketing': 'التسويق',
  'ch5-service': 'خدمة العملاء', 'ch6-management': 'الإدارة', 'ch7-production': 'الإنتاج', 'ch8-strategy': 'الاستراتيجية',
  'ch9-ethics': 'الأخلاقيات', 'ch10-future': 'المستقبل',
  'ch1-mindset': 'العقلية', 'ch2-programs': 'برامج الدعم', 'ch3-ideas': 'أفكار مشاريع', 'ch4-feasibility': 'دراسة الجدوى',
  'ch5-legal': 'التسجيل القانوني', 'ch6-financing': 'التمويل الحلال', 'ch7-marketing': 'التسويق', 'ch8-operations': 'العمليات',
  'ch9-quality': 'الجودة والشهادات', 'ch10-growth': 'النمو والتوسع',
  'day1-basics': 'اليوم 1: الأساسيات', 'day2-daily': 'اليوم 2: الحياة اليومية', 'day3-people': 'اليوم 3: الناس',
  'day4-places': 'اليوم 4: الأماكن', 'day5-food': 'اليوم 5: الأكل والتسوق', 'day6-actions': 'اليوم 6: الأفعال',
  'day7-comm': 'اليوم 7: التواصل',
  'dia1-bases': 'اليوم 1: الأساسيات', 'dia2-vida': 'اليوم 2: الحياة اليومية', 'dia3-gente': 'اليوم 3: الناس',
  'dia4-lugares': 'اليوم 4: الأماكن', 'dia5-verbos': 'اليوم 5: الأفعال', 'dia6-cultura': 'اليوم 6: الثقافة',
  'dia7-repaso': 'اليوم 7: المراجعة',
  'g1-basi': 'اليوم 1: الأساسيات', 'g2-vita': 'اليوم 2: الحياة اليومية', 'g3-gente': 'اليوم 3: الناس',
  'g4-luoghi': 'اليوم 4: الأماكن', 'g5-shopping': 'اليوم 5: التسوق والأفعال', 'g6-comunicare': 'اليوم 6: التواصل',
  'g7-cultura': 'اليوم 7: الثقافة',
  'fr1-bases': 'اليوم 1: الأساسيات', 'fr2-quotidien': 'اليوم 2: الحياة اليومية', 'fr3-gens': 'اليوم 3: الناس',
  'fr4-lieux': 'اليوم 4: الأماكن', 'fr5-achats': 'اليوم 5: التسوق والأفعال', 'fr6-comm': 'اليوم 6: التواصل',
  'fr7-culture': 'اليوم 7: الثقافة',
  'basic-colors': 'الألوان', 'mixing': 'الخلط', 'nature': 'الطبيعة', 'concepts': 'المفاهيم', 'games': 'الألعاب',
  'letters-1': 'الحروف أ-خ', 'letters-2': 'الحروف د-ش', 'letters-3': 'الحروف ص-ي', 'families': 'العائلات', 'words': 'الكلمات',
};

const lucideMap = {
  'cpu': LuCpu, 'brain': LuBrain, 'pen-tool': LuPenTool, 'bar-chart-3': LuChartBar,
  'x-circle': LuCircleX, 'zap': LuZap, 'coins': LuCoins, 'users': LuUsers,
  'globe': LuGlobe, 'globe-2': LuGlobe, 'building': LuBuilding, 'truck': LuTruck,
  'heart-pulse': LuHeartPulse, 'trending-up': LuTrendingUp, 'file-text': LuFileText,
  'palette': LuPalette, 'smartphone': LuSmartphone, 'megaphone': LuMegaphone,
  'calendar': LuCalendar, 'search': LuSearch, 'percent': LuPercent,
  'message-square': LuMessageSquare, 'sparkles': LuSparkles, 'mail': LuMail,
  'target': LuTarget, 'package': LuPackage, 'video': LuVideo, 'compass': LuCompass,
  'shield-check': LuShieldCheck, 'rocket': LuRocket,
};

/** Written book, PDF slides, and image summary — full-width layout (like a presentation) */
const WIDE_VIEWER_COMPONENTS = new Set(['content', 'presentation', 'summary']);

const TWEMOJI_BASE = 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/';
const emojiSplitRe = /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;
function twemojify(text) {
  if (!text) return text;
  const parts = text.split(emojiSplitRe);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (i % 2 === 1 && part) {
      const cp = [...part].map(c => c.codePointAt(0).toString(16)).filter(c => c !== 'fe0f').join('-');
      return <img key={i} src={`${TWEMOJI_BASE}${cp}.svg`} alt={part} style={{ display: 'inline', width: '1.2em', height: '1.2em', verticalAlign: '-0.15em', margin: '0 1px' }} onError={(e) => { e.target.outerHTML = part; }} />;
    }
    return part;
  });
}

function CourseViewerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  
  const [currentComponent, setCurrentComponent] = useState('video');
  const [componentProgress, setComponentProgress] = useState({
    video: false,
    content: false,
    presentation: false,
    flashcards: false,
    summary: false
  });

  // Flashcard state
  const [fcIndex, setFcIndex] = useState(0);
  const [fcFlipped, setFcFlipped] = useState(false);
  const [fcKnown, setFcKnown] = useState([]);
  const [fcReview, setFcReview] = useState([]);
  const [fcDone, setFcDone] = useState(false);
  
  // Mobile sidebar toggle
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  // Wide view toggle for content/presentation/summary
  const [isWideView, setIsWideView] = useState(true);

  // Scroll to top when component mounts or courseId changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    console.log('[COURSE VIEWER] 🔵 Effect triggered', { 
      authLoading, 
      hasUser: !!user, 
      courseId: id 
    });
    
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      console.log('[COURSE VIEWER] ⏳ Waiting for auth...');
      return;
    }
    
    console.log('[COURSE VIEWER] ✓ Auth loaded, checking user...');
    
    if (!user) {
      console.log('[COURSE VIEWER] ❌ No user - redirecting to login');
      navigate('/login');
      return;
    }
    
    console.log('[COURSE VIEWER] ✅ User found, loading course...');
    loadCourseAndProgress();
  }, [id, user, authLoading]);

  // Load saved component position on mount
  useEffect(() => {
    if (id && course) {
      const savedComponent = localStorage.getItem(`courseViewer_${id}_component`);
      const savedProgress = localStorage.getItem(`courseViewer_${id}_progress`);
      
      if (savedComponent) {
        setCurrentComponent(savedComponent);
      }
      
      if (savedProgress) {
        try {
          const progressData = JSON.parse(savedProgress);
          setComponentProgress(progressData);
        } catch (e) {
          console.error('Error parsing saved progress:', e);
        }
      }
    }
  }, [id, course]);

  // Save current component to localStorage whenever it changes
  useEffect(() => {
    if (id && course) {
      localStorage.setItem(`courseViewer_${id}_component`, currentComponent);
      localStorage.setItem(`courseViewer_${id}_progress`, JSON.stringify(componentProgress));
    }
  }, [currentComponent, componentProgress, id, course]);

  // Load saved flashcard state
  useEffect(() => {
    if (id && course && currentComponent === 'flashcards') {
      const savedFcIndex = localStorage.getItem(`courseViewer_${id}_fcIndex`);
      const savedFcKnown = localStorage.getItem(`courseViewer_${id}_fcKnown`);
      const savedFcReview = localStorage.getItem(`courseViewer_${id}_fcReview`);
      const savedFcDone = localStorage.getItem(`courseViewer_${id}_fcDone`);
      
      if (savedFcIndex) setFcIndex(parseInt(savedFcIndex, 10));
      if (savedFcKnown) {
        try {
          setFcKnown(JSON.parse(savedFcKnown));
        } catch (e) {
          console.error('Error parsing saved fcKnown:', e);
        }
      }
      if (savedFcReview) {
        try {
          setFcReview(JSON.parse(savedFcReview));
        } catch (e) {
          console.error('Error parsing saved fcReview:', e);
        }
      }
      if (savedFcDone) setFcDone(savedFcDone === 'true');
    }
  }, [id, course, currentComponent]);

  // Save flashcard state to localStorage whenever it changes
  useEffect(() => {
    if (id && course && currentComponent === 'flashcards') {
      localStorage.setItem(`courseViewer_${id}_fcIndex`, fcIndex.toString());
      localStorage.setItem(`courseViewer_${id}_fcKnown`, JSON.stringify(fcKnown));
      localStorage.setItem(`courseViewer_${id}_fcReview`, JSON.stringify(fcReview));
      localStorage.setItem(`courseViewer_${id}_fcDone`, fcDone.toString());
    }
  }, [fcIndex, fcKnown, fcReview, fcDone, id, course, currentComponent]);

  const loadCourseAndProgress = async () => {
    try {
      console.log('[COURSE VIEWER] 📚 Starting to load course data...');
      setLoading(true);
      
      const courseData = await apiService.getCourse(id);
      console.log('[COURSE VIEWER] ✅ Course loaded:', courseData.title_ar);
      setCourse(courseData);
      
      const progressData = await apiService.getProgress(id);
      console.log('[COURSE VIEWER] ✅ Progress loaded');
      setProgress(progressData);
      
      setError(null);
    } catch (err) {
      console.error('[COURSE VIEWER] ❌ Error loading:', err.message);
      if (err.message && err.message.includes('403')) {
        setError('يجب شراء الدورة أولاً');
        setTimeout(() => navigate(`/course/${id}`), 2000);
      } else {
        setError('فشل تحميل الدورة');
      }
    } finally {
      console.log('[COURSE VIEWER] 🏁 Load complete - setting loading to false');
      setLoading(false);
    }
  };

  const markComponentComplete = (component) => {
    setComponentProgress(prev => ({
      ...prev,
      [component]: true
    }));
  };

  const getNextComponent = () => {
    const order = ['video', 'content', 'presentation', 'flashcards', 'summary'];
    const currentIndex = order.indexOf(currentComponent);
    if (currentIndex < order.length - 1) {
      return order[currentIndex + 1];
    }
    return null;
  };

  const handleNext = () => {
    markComponentComplete(currentComponent);
    const next = getNextComponent();
    if (next) {
      setCurrentComponent(next);
    }
  };

  const calculateOverallProgress = () => {
    const completed = Object.values(componentProgress).filter(Boolean).length;
    return Math.round((completed / 5) * 100);
  };

  const renderComponentContent = () => {
    const wideCard =
      'bg-white rounded-none sm:rounded-xl p-2 sm:p-4 md:p-6 shadow-lg border border-gray-100 border-x-0 sm:border-x';

    switch (currentComponent) {
      case 'video':
        return (
          <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <FaPlay className="text-primary text-2xl md:text-3xl" />
              <h2 className="text-xl md:text-2xl font-bold text-text">فيديو توضيحي - {course.title_ar}</h2>
            </div>

            {course.video_explainer_url ? (
              <VideoPlayer
                src={apiService.getProtectedContentUrl(course.id, 'video')}
                title={`فيديو توضيحي - ${course.title_ar}`}
              />
            ) : (
              <div className="aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <p className="text-gray-500 text-sm md:text-lg">الفيديو قيد التحضير...</p>
              </div>
            )}
            
            {/* Simple explanation text - below video */}
            <div className="mt-4 md:mt-6 mb-4">
              <p className="text-gray-600 text-sm md:text-base flex items-start gap-2">
                <FaLightbulb className="text-primary mt-0.5 text-lg flex-shrink-0" />
                <span>شاهد الفيديو التوضيحي لفهم المفاهيم الأساسية بطريقة مبسطة وممتعة.</span>
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <p className="text-gray-600 text-xs md:text-base flex items-center gap-2">
                <LuSparkles className="text-primary flex-shrink-0" />
                <span>شاهد الفيديو بتركيز ثم انتقل للخطوة التالية</span>
              </p>
              <button
                onClick={handleNext}
                className="w-full md:w-auto bg-primary text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 shadow-md text-sm md:text-base"
              >
                التالي: المحتوى المكتوب
              </button>
            </div>
          </div>
        );

      case 'content':
        return (
          <div className={wideCard}>
            <div className="flex items-center justify-between gap-3 mb-4 md:mb-6 px-1 sm:px-0">
              <div className="flex items-center gap-3">
                <FaFileAlt className="text-primary text-2xl md:text-3xl" />
                <h2 className="text-xl md:text-2xl font-bold text-text">محتوى مكتوب تفاعلي</h2>
              </div>
              <button
                onClick={() => setIsWideView(!isWideView)}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                title={isWideView ? 'عرض عادي' : 'عرض عريض'}
              >
                {isWideView ? <FaCompress size={16} /> : <FaExpand size={16} />}
                <span className="hidden md:inline">{isWideView ? 'عرض عادي' : 'عرض عريض'}</span>
              </button>
            </div>

            {course.file_url && course.file_type === 'html' ? (
              <BookReader
                bookUrl={course.file_url}
                courseId={course.id}
                isPurchased={true}
                onComplete={() => markComponentComplete('content')}
              />
            ) : (
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-gray-500 text-sm md:text-base">المحتوى قيد التحضير...</p>
              </div>
            )}

            {/* Simple explanation text */}
            <div className="mt-4 md:mt-6 mb-4">
              <p className="text-gray-600 text-sm md:text-base flex items-start gap-2">
                <FaBookOpen className="text-green-600 mt-0.5 text-lg flex-shrink-0" />
                <span>اقرأ المحتوى بتمعن وتعلم كل التفاصيل. يمكنك التنقل بين الصفحات والرجوع لأي جزء.</span>
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <button
                onClick={() => setCurrentComponent('video')}
                className="text-gray-600 hover:text-primary transition-colors font-medium text-sm md:text-base"
              >
                ← السابق
              </button>
              <button
                onClick={handleNext}
                className="w-full md:w-auto bg-primary text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 shadow-md text-sm md:text-base"
              >
                التالي: العرض التقديمي
              </button>
            </div>
          </div>
        );

      case 'presentation':
        return (
          <div className={wideCard}>
            <div className="flex items-center justify-between gap-3 mb-4 md:mb-6 px-1 sm:px-0">
              <div className="flex items-center gap-3">
                <FaDesktop className="text-primary text-2xl md:text-3xl" />
                <h2 className="text-xl md:text-2xl font-bold text-text">عرض تقديمي منظم</h2>
              </div>
              <button
                onClick={() => setIsWideView(!isWideView)}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                title={isWideView ? 'عرض عادي' : 'عرض عريض'}
              >
                {isWideView ? <FaCompress size={16} /> : <FaExpand size={16} />}
                <span className="hidden md:inline">{isWideView ? 'عرض عادي' : 'عرض عريض'}</span>
              </button>
            </div>

            {course.presentation_url ? (
              <PresentationViewer
                src={apiService.getProtectedContentUrl(course.id, 'presentation')}
                title={`عرض تقديمي - ${course.title_ar}`}
              />
            ) : (
              <div className="aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <p className="text-gray-500 text-sm md:text-lg">العرض التقديمي قيد التحضير...</p>
              </div>
            )}

            {/* Simple explanation text */}
            <div className="mt-4 md:mt-6 mb-4">
              <p className="text-gray-600 text-sm md:text-base flex items-start gap-2">
                <FaDesktop className="text-purple-600 mt-0.5 text-lg flex-shrink-0" />
                <span>العرض التقديمي يلخص أهم النقاط بطريقة منظمة. راجع الشرائح لتثبيت المعلومات.</span>
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <button
                onClick={() => setCurrentComponent('content')}
                className="text-gray-600 hover:text-primary transition-colors font-medium text-sm md:text-base"
              >
                ← السابق
              </button>
              <button
                onClick={handleNext}
                className="w-full md:w-auto bg-primary text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 shadow-md text-sm md:text-base"
              >
                التالي: البطاقات التعليمية
              </button>
            </div>
          </div>
        );

      case 'flashcards': {
        const cards = getFlashcards(parseInt(id));
        const iconType = getIconType(parseInt(id));
        const totalCards = cards.length;

        if (totalCards === 0) {
          return (
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <FaStar className="text-yellow-500 text-3xl" />
                <h2 className="text-2xl font-bold text-text">بطاقات تعليمية</h2>
              </div>
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-gray-600 text-lg mb-4">البطاقات قيد التحضير...</p>
                <p className="text-gray-500 text-sm">قريباً ستتمكن من المراجعة!</p>
              </div>
              <div className="mt-6 flex justify-between items-center">
                <button onClick={() => setCurrentComponent('presentation')} className="text-gray-600 hover:text-primary transition-colors font-medium">← السابق</button>
                <button onClick={handleNext} className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 shadow-md">التالي: الملخص الشامل</button>
              </div>
            </div>
          );
        }

        if (fcDone) {
          const knownPct = Math.round((fcKnown.length / totalCards) * 100);
          return (
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  {knownPct >= 80
                    ? <LuTrophy size={56} className="text-yellow-500" strokeWidth={1.5} />
                    : knownPct >= 50
                      ? <LuThumbsUp size={56} className="text-blue-500" strokeWidth={1.5} />
                      : <LuFlame size={56} className="text-orange-500" strokeWidth={1.5} />
                  }
                </div>
                <h2 className="text-3xl font-black text-text mb-2">أحسنت! كملت البطاقات</h2>
                <p className="text-gray-600 text-lg">النتيجة تاعك:</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                  <div className="text-4xl font-black text-green-600 mb-1">{fcKnown.length}</div>
                  <div className="text-green-700 font-bold flex items-center justify-center gap-1"><FaCheck className="text-sm" /> عرفتها</div>
                </div>
                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-center">
                  <div className="text-4xl font-black text-orange-600 mb-1">{fcReview.length}</div>
                  <div className="text-orange-700 font-bold flex items-center justify-center gap-1"><FaRedo className="text-sm" /> نراجعها</div>
                </div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div className="bg-green-500 h-4 rounded-full transition-all" style={{ width: `${knownPct}%` }}></div>
              </div>
              <p className="text-center text-gray-600 mb-6">{knownPct}% إتقان</p>

              {fcReview.length > 0 && (
                <button
                  onClick={() => { setFcIndex(0); setFcFlipped(false); setFcKnown([]); setFcReview([]); setFcDone(false); }}
                  className="w-full bg-orange-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-600 transition-all duration-300 shadow-md flex items-center justify-center gap-2 mb-3"
                >
                  <FaRedo /> أعد البطاقات اللي ما عرفتهاش
                </button>
              )}

              <div className="flex justify-between items-center mt-4">
                <button onClick={() => setCurrentComponent('presentation')} className="text-gray-600 hover:text-primary transition-colors font-medium">← السابق</button>
                <button onClick={handleNext} className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 shadow-md">التالي: الملخص الشامل</button>
              </div>
            </div>
          );
        }

        const card = cards[fcIndex];
        const processedCount = fcKnown.length + fcReview.length;

        const handleKnew = () => {
          setFcKnown(prev => [...prev, card.id]);
          setFcFlipped(false);
          if (fcIndex < totalCards - 1) {
            setTimeout(() => setFcIndex(prev => prev + 1), 200);
          } else {
            setFcDone(true);
            markComponentComplete('flashcards');
          }
        };

        const handleReview = () => {
          setFcReview(prev => [...prev, card.id]);
          setFcFlipped(false);
          if (fcIndex < totalCards - 1) {
            setTimeout(() => setFcIndex(prev => prev + 1), 200);
          } else {
            setFcDone(true);
            markComponentComplete('flashcards');
          }
        };

        const cardColors = [
          { from: '#3B82F6', to: '#1D4ED8', accent: '#DBEAFE' },
          { from: '#8B5CF6', to: '#6D28D9', accent: '#EDE9FE' },
          { from: '#EC4899', to: '#BE185D', accent: '#FCE7F3' },
          { from: '#F59E0B', to: '#D97706', accent: '#FEF3C7' },
          { from: '#10B981', to: '#047857', accent: '#D1FAE5' },
          { from: '#EF4444', to: '#B91C1C', accent: '#FEE2E2' },
          { from: '#06B6D4', to: '#0E7490', accent: '#CFFAFE' },
          { from: '#F97316', to: '#C2410C', accent: '#FFEDD5' },
        ];
        const colorScheme = cardColors[fcIndex % cardColors.length];

        return (
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2 md:gap-3">
                <FaStar className="text-yellow-500 text-xl md:text-2xl" />
                <h2 className="text-lg md:text-xl font-bold text-text">بطاقات تعليمية</h2>
              </div>
              <span className="bg-primary/10 text-primary px-2 md:px-3 py-1 md:py-1.5 rounded-full font-bold text-xs md:text-sm">
                {fcIndex + 1} / {totalCards}
              </span>
            </div>

            {/* Explanation - Show before first card only */}
            {fcIndex === 0 && !fcFlipped && (
              <div className="mb-4">
                <p className="text-gray-600 text-sm md:text-base flex items-start gap-2">
                  <FaBolt className="text-yellow-600 mt-0.5 text-lg flex-shrink-0" />
                  <span>اقرأ السؤال، اضغط على البطاقة لرؤية الإجابة، ثم اختر "عرفتها!" أو "نراجعها".</span>
                </p>
              </div>
            )}

            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4 md:mb-6">
              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${((processedCount) / totalCards) * 100}%`, background: `linear-gradient(90deg, ${colorScheme.from}, ${colorScheme.to})` }}></div>
            </div>

            <div className="flex flex-col items-center">
              {/* Vertical card */}
              <div
                className="relative cursor-pointer select-none mb-5"
                style={{ perspective: '1200px', width: '280px', height: '420px' }}
                onClick={() => setFcFlipped(!fcFlipped)}
              >
                <div
                  className="relative w-full h-full transition-transform duration-600"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: fcFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {/* Front */}
                  <div
                    className="absolute inset-0 rounded-3xl flex flex-col items-center justify-between overflow-hidden"
                    style={{
                      backfaceVisibility: 'hidden',
                      background: `linear-gradient(135deg, ${colorScheme.from}, ${colorScheme.to})`,
                      boxShadow: `0 20px 40px -12px ${colorScheme.from}66, 0 8px 16px -8px rgba(0,0,0,0.1)`,
                    }}
                  >
                    {/* Card number badge */}
                    <div className="w-full flex justify-between items-start p-5">
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
                        <span className="text-white/90 font-bold text-xs">{String(fcIndex + 1).padStart(2, '0')}</span>
                      </div>
                      <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
                        <span className="text-white/90 font-medium text-xs">{categoryLabels[card.category] || card.category}</span>
                      </div>
                    </div>

                    {/* Icon area */}
                    <div className="flex-1 flex items-center justify-center px-6">
                      <div className="bg-white/15 backdrop-blur-sm rounded-full p-6" style={{ boxShadow: 'inset 0 2px 8px rgba(255,255,255,0.2)' }}>
                        {iconType === ICON_TYPE.LUCIDE && card.front.icon ? (() => {
                          const IconComp = lucideMap[card.front.icon];
                          return IconComp ? <IconComp className="text-white drop-shadow-lg" size={56} strokeWidth={1.5} /> : null;
                        })() : card.front.emoji ? (
                          <img
                            src={`https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${[...card.front.emoji].map(c => c.codePointAt(0).toString(16)).filter(c => c !== 'fe0f').join('-')}.svg`}
                            alt=""
                            className="w-14 h-14 drop-shadow-lg"
                            onError={(e) => { e.target.style.fontSize = '3.5rem'; e.target.style.width = 'auto'; e.target.style.height = 'auto'; e.target.outerHTML = `<span class="text-6xl drop-shadow-lg">${card.front.emoji}</span>`; }}
                          />
                        ) : null}
                      </div>
                    </div>

                    {/* Text area */}
                    <div className="w-full p-5 pt-0">
                      <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4">
                        <p className="text-white font-bold text-center leading-relaxed" style={{ fontSize: card.front.text.length > 40 ? '0.85rem' : '1rem' }}>
                          {iconType === ICON_TYPE.TWEMOJI ? twemojify(card.front.text) : card.front.text}
                        </p>
                      </div>
                      <p className="text-white/50 text-center text-xs mt-3">اضغط لقلب البطاقة</p>
                    </div>
                  </div>

                  {/* Back */}
                  <div
                    className="absolute inset-0 rounded-3xl flex flex-col items-center justify-between overflow-hidden"
                    style={{
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      background: `linear-gradient(135deg, #f8fafc, #f1f5f9)`,
                      boxShadow: `0 20px 40px -12px rgba(0,0,0,0.15), 0 0 0 2px ${colorScheme.from}33`,
                    }}
                  >
                    {/* Colored top accent */}
                    <div className="w-full h-2 rounded-t-3xl" style={{ background: `linear-gradient(90deg, ${colorScheme.from}, ${colorScheme.to})` }}></div>

                    <div className="flex-1 flex flex-col items-center justify-center px-5 py-4 w-full">
                      <h3 className="text-xl font-black text-gray-900 mb-2 text-center leading-tight">{iconType === ICON_TYPE.TWEMOJI ? twemojify(card.back.title) : card.back.title}</h3>

                      <div className="w-12 h-0.5 rounded-full my-2" style={{ background: `linear-gradient(90deg, ${colorScheme.from}, ${colorScheme.to})` }}></div>

                      <p className="text-sm text-gray-500 font-medium mb-4 text-center">{iconType === ICON_TYPE.TWEMOJI ? twemojify(card.back.subtitle) : card.back.subtitle}</p>

                      <div className="rounded-xl px-4 py-3 w-full border" style={{ background: colorScheme.accent, borderColor: `${colorScheme.from}22` }}>
                        <p className="text-gray-700 text-center text-sm leading-relaxed">{iconType === ICON_TYPE.TWEMOJI ? twemojify(card.back.detail) : card.back.detail}</p>
                      </div>
                    </div>

                    <div className="w-full px-5 pb-4">
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-2 h-2 rounded-full" style={{ background: colorScheme.from }}></div>
                        <div className="w-2 h-2 rounded-full" style={{ background: colorScheme.from, opacity: 0.5 }}></div>
                        <div className="w-2 h-2 rounded-full" style={{ background: colorScheme.from, opacity: 0.25 }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              {fcFlipped ? (
                <div className="flex gap-3 w-full max-w-xs mb-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleReview(); }}
                    className="flex-1 border-2 border-orange-300 text-orange-600 px-4 py-3 rounded-2xl font-bold hover:bg-orange-50 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaRedo className="text-sm" /> نراجعها
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleKnew(); }}
                    className="flex-1 border-2 border-green-400 text-green-600 px-4 py-3 rounded-2xl font-bold hover:bg-green-50 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaCheck className="text-sm" /> عرفتها!
                  </button>
                </div>
              ) : (
                <p className="text-center text-gray-400 text-xs mb-4">اضغط على البطاقة لرؤية الإجابة</p>
              )}
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <button onClick={() => setCurrentComponent('presentation')} className="text-gray-500 hover:text-primary transition-colors font-medium text-sm">← السابق</button>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-green-600"><FaCheck /> {fcKnown.length}</span>
                <span className="flex items-center gap-1 text-orange-500"><FaRedo /> {fcReview.length}</span>
              </div>
            </div>
          </div>
        );
      }

      case 'summary':
        return (
          <div className={wideCard}>
            <div className="flex items-center justify-between gap-3 mb-4 md:mb-6 px-1 sm:px-0">
              <div className="flex items-center gap-2 md:gap-3">
                <FaImage className="text-primary text-2xl md:text-3xl" />
                <h2 className="text-xl md:text-2xl font-bold text-text">ملخص شامل للدورة</h2>
              </div>
              <button
                onClick={() => setIsWideView(!isWideView)}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                title={isWideView ? 'عرض عادي' : 'عرض عريض'}
              >
                {isWideView ? <FaCompress size={16} /> : <FaExpand size={16} />}
                <span className="hidden md:inline">{isWideView ? 'عرض عادي' : 'عرض عريض'}</span>
              </button>
            </div>

            {course.summary_image_url ? (
              <div className="relative w-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                {/* Top bar - like BookReader */}
                <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-800 text-white" dir="rtl">
                  <div className="flex items-center gap-2">
                    <img src="/icon.png" alt="Allemni" className="w-5 h-5 sm:w-6 sm:h-6 rounded" />
                    <span className="font-bold text-xs sm:text-sm truncate">ملخص شامل - {course.title_ar}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/70 text-xs sm:text-sm">صفحة 1 من 1</span>
                  </div>
                </div>

                {/* Image content area */}
                <div className="relative bg-white p-2 sm:p-4" style={{ minHeight: '400px' }}>
                  <img
                    src={apiService.getProtectedContentUrl(course.id, 'summary')}
                    alt="ملخص الدورة"
                    className="w-full h-auto block"
                    referrerPolicy="origin"
                  />
                </div>

                {/* Bottom bar - like BookReader */}
                <div className="flex items-center justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2.5 bg-gray-800" dir="ltr">
                  <button
                    disabled
                    className="px-2 py-1.5 rounded-lg text-xs font-bold bg-gray-700 text-gray-500 cursor-not-allowed"
                  >
                    «
                  </button>
                  <button
                    disabled
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-700 text-gray-500 cursor-not-allowed"
                  >
                    <span className="flex items-center gap-1">← السابق</span>
                  </button>

                  <span className="text-white/70 text-sm font-mono">1 / 1</span>

                  <button
                    disabled
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-700 text-gray-500 cursor-not-allowed"
                  >
                    <span className="flex items-center gap-1">التالي →</span>
                  </button>
                  <button
                    disabled
                    className="px-2 py-1.5 rounded-lg text-xs font-bold bg-gray-700 text-gray-500 cursor-not-allowed"
                  >
                    »
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <FaImage className="text-gray-400 text-6xl mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">الملخص قيد التحضير...</p>
                <p className="text-gray-400 text-sm mt-2">سيتم إضافته قريباً</p>
              </div>
            )}

            {/* Simple explanation text */}
            <div className="mt-4 md:mt-6 mb-4 px-1 sm:px-0">
              <p className="text-gray-600 text-sm md:text-base flex items-start gap-2">
                <FaAward className="text-indigo-600 mt-0.5 text-lg flex-shrink-0" />
                <span>ملخص شامل لكل ما تعلمته. احفظه للمراجعة السريعة في أي وقت.</span>
              </p>
            </div>

            <div className="flex justify-between items-center px-1 sm:px-0">
              <button
                onClick={() => setCurrentComponent('flashcards')}
                className="text-gray-600 hover:text-primary transition-colors font-medium"
              >
                ← السابق
              </button>
              <button
                onClick={() => {
                  markComponentComplete('summary');
                  // Course is now 100% complete
                }}
                className="bg-green-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-700 transition-all duration-300 shadow-md flex items-center gap-2"
              >
                <FaCheckCircle />
                إنهاء الدورة
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
          <p className="mt-4 text-text text-lg">جاري تحميل الدورة...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold mb-4 text-text">{error || 'الدورة غير موجودة'}</h2>
          <Link to="/courses" className="text-primary hover:underline font-medium">
            العودة إلى الدورات
          </Link>
        </div>
      </div>
    );
  }

  const components = [
    { id: 'video', icon: FaPlay, label: 'فيديو توضيحي', order: 1 },
    { id: 'content', icon: FaFileAlt, label: 'محتوى مكتوب', order: 2 },
    { id: 'presentation', icon: FaDesktop, label: 'عرض تقديمي', order: 3 },
    { id: 'flashcards', icon: FaStar, label: 'بطاقات تعليمية', order: 4 },
    { id: 'summary', icon: FaImage, label: 'ملخص شامل', order: 5 }
  ];

  const isWideCourseViewer = WIDE_VIEWER_COMPONENTS.has(currentComponent) && isWideView;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4">
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <Link 
                to="/my-content"
                className="text-text3 hover:text-primary transition-colors flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                <FaHome />
                <span className="font-medium">محتواي التعليمي</span>
              </Link>
              <span className="text-gray-300">|</span>
              <Link 
                to={`/course/${id}`}
                className="text-text3 hover:text-primary transition-colors flex items-center gap-2"
              >
                <FaBook />
                <span>تفاصيل الدورة</span>
              </Link>
            </div>

            <div className="text-center flex-1 mx-4">
              <h1 className="text-text font-bold text-xl truncate">
                {course.title_ar}
              </h1>
              <div className="flex items-center justify-center gap-2 mt-1">
                <div className="bg-primary/10 rounded-full px-3 py-1">
                  <span className="text-primary font-bold text-sm">{calculateOverallProgress()}%</span>
                </div>
              </div>
            </div>

            <div className="w-32"></div>
          </div>

          {/* Mobile Header with Learning Path Navigation */}
          <div className="md:hidden">
            {/* Top Row - Back button, Course Title and Menu */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <Link 
                to="/my-content"
                className="flex items-center gap-2 text-text3 hover:text-primary transition-colors bg-gray-100 px-3 py-1.5 rounded-lg"
              >
                <FaHome size={18} />
              </Link>
              
              <div className="flex-1 mx-2 text-center">
                <h1 className="text-text font-bold text-sm truncate">{course.title_ar}</h1>
              </div>
              
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-lg"
              >
                <FaBook size={14} />
                <span className="text-xs font-bold">{calculateOverallProgress()}%</span>
              </button>
            </div>

            {/* Learning Path Navigation - Horizontal Scroll */}
            <div className="overflow-x-auto scrollbar-hide py-2">
              <div className="flex gap-2 min-w-max px-1">
                {components.map((comp, index) => {
                  const Icon = comp.icon;
                  const isActive = currentComponent === comp.id;
                  const isCompleted = componentProgress[comp.id];
                  
                  return (
                    <button
                      key={comp.id}
                      onClick={() => setCurrentComponent(comp.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg font-bold text-xs whitespace-nowrap transition-all ${
                        isActive 
                          ? 'bg-primary text-white shadow-md' 
                          : isCompleted 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <Icon size={14} />
                      <span>{index + 1}</span>
                      {isCompleted && <FaCheck size={10} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content — wider column for book / slides / summary (عرض عريض) */}
      <div
        className={
          isWideCourseViewer
            ? 'w-full max-w-[min(100vw,1920px)] mx-auto px-0 sm:px-4 lg:px-6 xl:px-8 py-6 md:py-8'
            : 'container mx-auto px-4 py-8'
        }
      >
        {/* Mobile Sidebar Overlay */}
        {showMobileSidebar && (
          <div 
            className="md:hidden fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowMobileSidebar(false)}
          >
            <div 
              className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-text">مسار التعلم</h3>
                  <button
                    onClick={() => setShowMobileSidebar(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">التقدم الإجمالي</span>
                    <span className="text-primary font-bold">{calculateOverallProgress()}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all duration-300"
                      style={{ width: `${calculateOverallProgress()}%` }}
                    ></div>
                  </div>
                </div>

                {/* Components List */}
                <div className="space-y-3">
                  {components.map((comp) => {
                    const Icon = comp.icon;
                    const isActive = currentComponent === comp.id;
                    const isCompleted = componentProgress[comp.id];
                    
                    return (
                      <button
                        key={comp.id}
                        onClick={() => {
                          setCurrentComponent(comp.id);
                          setShowMobileSidebar(false);
                        }}
                        className={`w-full text-right p-4 rounded-lg transition-all duration-300 ${
                          isActive 
                            ? 'bg-primary text-white shadow-lg' 
                            : isCompleted
                            ? 'bg-green-50 text-green-700 border-2 border-green-200'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Icon className={`text-xl ${isActive ? 'text-white' : isCompleted ? 'text-green-600' : 'text-gray-400'}`} />
                            <div>
                              <div className="font-bold text-sm">{comp.label}</div>
                              <div className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>{comp.order}</div>
                            </div>
                          </div>
                          {isCompleted && (
                            <FaCheckCircle className={`text-lg ${isActive ? 'text-white' : 'text-green-600'}`} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`flex flex-col lg:flex-row ${isWideCourseViewer ? 'gap-4 lg:gap-5' : 'gap-6'}`}>
          {/* Main Area - Left/Center */}
          <div className="flex-1 order-1 min-w-0">
            {renderComponentContent()}
                  </div>

          {/* Progress Sidebar - Right (Desktop Only) */}
          <div className="hidden lg:block w-80 order-2">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 sticky top-4">
              <h3 className="text-xl font-bold text-text mb-4">مسار التعلم</h3>
              
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">التقدم الإجمالي</span>
                  <span className="text-primary font-bold">{calculateOverallProgress()}%</span>
                  </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all duration-300"
                    style={{ width: `${calculateOverallProgress()}%` }}
                  ></div>
            </div>
            </div>

              {/* Components List */}
              <div className="space-y-3">
                {components.map((comp) => {
                  const Icon = comp.icon;
                  const isActive = currentComponent === comp.id;
                  const isCompleted = componentProgress[comp.id];
                  
                  return (
            <button
                      key={comp.id}
                      onClick={() => setCurrentComponent(comp.id)}
                      className={`w-full text-right p-4 rounded-lg transition-all duration-300 ${
                        isActive 
                          ? 'bg-primary text-white shadow-lg' 
                          : isCompleted
                          ? 'bg-green-50 text-green-700 border-2 border-green-200'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isActive ? 'bg-white/20' : isCompleted ? 'bg-green-600' : 'bg-gray-300'
                          }`}>
                            {isCompleted ? (
                              <FaCheckCircle className="text-white" />
                            ) : (
                              <span className={`text-sm font-bold ${isActive ? 'text-white' : 'text-gray-700'}`}>{comp.order}</span>
                            )}
                          </div>
                          <div>
                            <div className={`font-bold ${isActive ? 'text-white' : ''}`}>
                              {comp.label}
                            </div>
                          </div>
                        </div>
                        <Icon className={`text-xl ${isActive ? 'text-white' : isCompleted ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            </button>
                  );
                })}
              </div>

              {/* Completion Status */}
              {calculateOverallProgress() === 100 && (
                <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                  <FaCheckCircle className="text-green-600 text-3xl mx-auto mb-2" />
                  <p className="text-green-700 font-bold">أحسنت! أكملت الدورة</p>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseViewerPage;
